/**
 * Chart — graphique simple pour les questions de lecture de documents chiffrés (SVG, sans dépendance).
 *
 * data: {
 *   kind: 'bar' | 'line' | 'pie',
 *   title: "Titre",
 *   unit: "€" | "%" | "dossiers"…,           // affiché après chaque valeur
 *   labels: ["janv.", "févr.", …],             // bar / line : abscisses ; pie : parts
 *   series: [{ name: "2025", values: [120, 150, …] }],  // bar / line : 1 ou 2 séries ; pie : 1 série
 *   y_min: 0,                                  // facultatif : axe qui ne part pas de zéro (piège de lecture voulu)
 *   source: "Données fictives"
 * }
 * Les valeurs sont écrites sur le graphique : la question porte sur l'interprétation, pas sur la lecture d'une graduation.
 *
 * Mode repère (kind: 'xy', 27/10/2026) : courbes de fonctions dans un repère gradué, pour lire une image,
 * un antécédent, un signe, une pente ou une tangente. La lecture d'une graduation est ici voulue.
 * data: {
 *   kind: 'xy', title,
 *   x: { min: -3, max: 5, step: 1, label: 'x' }, y: { min: -2, max: 6, step: 1, label: 'y' },
 *   curves: [ { name: 'f', fn: 'affine', a: 2, b: -1 }                  // y = ax + b
 *           | { name: 'g', fn: 'quadratic', a: 1, b: 0, c: -2 }        // y = ax² + bx + c
 *           | { name: 'h', fn: 'points', points: [[x, y], …] }         // ligne brisée
 *           ; style: 'dashed' facultatif (ex. tangente) ],
 *   marks: [ { x: 2, y: 3, label: 'A', pos: 'nw' } ]                   // points remarquables ; pos = ne|nw|se|sw
 * }
 * Aucune expression n'est évaluée : seules ces trois familles sont tracées.
 */

import { useId } from 'react';

const COULEURS = ['#7c3aed', '#f59e0b', '#0ea5e9', '#10b981', '#ef4444', '#64748b'];
const W = 400;
const H = 240;
const M = { haut: 22, droite: 8, bas: 30, gauche: 40 };

const nombre = (v) => (typeof v === 'number' ? v.toLocaleString('fr-FR') : String(v ?? ''));
const avecUnite = (v, unit) => {
    if (!unit) return nombre(v);
    return unit === '%' || unit === '€' ? `${nombre(v)} ${unit}` : `${nombre(v)} ${unit}`;
};

// Graduation « ronde » (1, 2, 2,5 ou 5 × 10^n) pour environ 5 intervalles.
function pas(etendue) {
    if (etendue <= 0) return 1;
    const brut = etendue / 5;
    const p = 10 ** Math.floor(Math.log10(brut));
    const r = brut / p;
    return (r <= 1 ? 1 : r <= 2 ? 2 : r <= 2.5 ? 2.5 : r <= 5 ? 5 : 10) * p;
}

function Axes({ yMin, yMax, step, y }) {
    const ticks = [];
    for (let v = yMin; v <= yMax + step / 1000; v += step) ticks.push(Math.round(v * 1000) / 1000);
    return (
        <g className="qv-chart__axes">
            {ticks.map(t => (
                <g key={t}>
                    <line x1={M.gauche} x2={W - M.droite} y1={y(t)} y2={y(t)} className="qv-chart__grid" />
                    <text x={M.gauche - 5} y={y(t) + 3.5} textAnchor="end" className="qv-chart__tick">{nombre(t)}</text>
                </g>
            ))}
            <line x1={M.gauche} x2={M.gauche} y1={M.haut} y2={H - M.bas} className="qv-chart__axis" />
        </g>
    );
}

function BarresOuCourbe({ data, kind }) {
    const labels = data.labels || [];
    const series = (data.series || []).filter(s => Array.isArray(s.values)).slice(0, 2);
    const toutes = series.flatMap(s => s.values).filter(v => typeof v === 'number');
    if (!labels.length || !toutes.length) return null;
    const yMin = typeof data.y_min === 'number' ? data.y_min : Math.min(0, ...toutes);
    const step = pas(Math.max(...toutes) - yMin);
    const yMax = yMin + Math.ceil((Math.max(...toutes) - yMin) / step) * step || yMin + step;
    const y = v => H - M.bas - ((v - yMin) / (yMax - yMin)) * (H - M.haut - M.bas);
    const largeur = (W - M.gauche - M.droite) / labels.length;
    const xCentre = i => M.gauche + largeur * (i + 0.5);
    const barre = Math.min(34, (largeur * 0.7) / series.length);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="qv-chart__svg" role="img" aria-label={data.title || 'Graphique'}>
            <Axes yMin={yMin} yMax={yMax} step={step} y={y} />
            {labels.map((l, i) => (
                <text key={l + i} x={xCentre(i)} y={H - M.bas + 14} textAnchor="middle" className="qv-chart__label">{l}</text>
            ))}
            {series.map((s, si) => {
                const couleur = COULEURS[si];
                if (kind === 'line') {
                    const points = s.values.map((v, i) => `${xCentre(i)},${y(v)}`).join(' ');
                    return (
                        <g key={si}>
                            <polyline points={points} fill="none" stroke={couleur} strokeWidth="2.5" strokeLinejoin="round" />
                            {s.values.map((v, i) => (
                                <g key={i}>
                                    <circle cx={xCentre(i)} cy={y(v)} r="3.5" fill={couleur} />
                                    <text x={xCentre(i)} y={si === 0 ? y(v) - 7 : y(v) + 15} textAnchor="middle" className="qv-chart__value" fill={couleur}>{nombre(v)}</text>
                                </g>
                            ))}
                        </g>
                    );
                }
                return (
                    <g key={si}>
                        {s.values.map((v, i) => {
                            const x = xCentre(i) - (barre * series.length) / 2 + si * barre;
                            const haut = Math.min(y(v), y(Math.max(yMin, 0)));
                            const h = Math.abs(y(Math.max(yMin, 0)) - y(v));
                            return (
                                <g key={i}>
                                    <rect x={x + 1} y={haut} width={barre - 2} height={Math.max(h, 0.5)} rx="2" fill={couleur} />
                                    <text x={x + barre / 2} y={haut - 4} textAnchor="middle" className="qv-chart__value">{nombre(v)}</text>
                                </g>
                            );
                        })}
                    </g>
                );
            })}
        </svg>
    );
}

// Repère gradué (kind 'xy') : W × HR, axes aux zéros quand ils sont visibles.
const HR = 300;
const MR = { haut: 14, droite: 16, bas: 22, gauche: 30 };
const valeurEn = (c, x) => c.fn === 'affine' ? (c.a ?? 0) * x + (c.b ?? 0)
    : c.fn === 'quadratic' ? (c.a ?? 0) * x * x + (c.b ?? 0) * x + (c.c ?? 0) : null;

function Repere({ data }) {
    const clip = 'rep' + useId().replace(/[^a-zA-Z0-9]/g, '');
    const X = data.x || {};
    const Y = data.y || {};
    if (![X.min, X.max, Y.min, Y.max].every(v => typeof v === 'number') || X.max <= X.min || Y.max <= Y.min) return null;
    const sx = X.step > 0 ? X.step : 1;
    const sy = Y.step > 0 ? Y.step : 1;
    const px = v => MR.gauche + ((v - X.min) / (X.max - X.min)) * (W - MR.gauche - MR.droite);
    const py = v => HR - MR.bas - ((v - Y.min) / (Y.max - Y.min)) * (HR - MR.haut - MR.bas);
    const graduations = (min, max, step) => {
        const t = [];
        for (let v = Math.ceil(min / step - 1e-9) * step; v <= max + 1e-9; v += step) t.push(Math.round(v * 1000) / 1000);
        return t;
    };
    const tx = graduations(X.min, X.max, sx);
    const ty = graduations(Y.min, Y.max, sy);
    const ax = Y.min <= 0 && Y.max >= 0 ? py(0) : HR - MR.bas; // axe des abscisses
    const ay = X.min <= 0 && X.max >= 0 ? px(0) : MR.gauche;   // axe des ordonnées
    const courbes = (data.curves || []).slice(0, 3).map((c, i) => {
        let pts = [];
        if (c.fn === 'points') pts = (c.points || []).filter(p => Array.isArray(p) && p.length === 2);
        else if (c.fn === 'affine' || c.fn === 'quadratic') {
            for (let k = 0; k <= 200; k++) { const x = X.min + (k / 200) * (X.max - X.min); pts.push([x, valeurEn(c, x)]); }
        }
        // Nom de la courbe au dernier point visible.
        const visibles = pts.filter(([x, y]) => y >= Y.min && y <= Y.max && x >= X.min && x <= X.max);
        return { c, pts, fin: visibles[visibles.length - 1], couleur: COULEURS[i] };
    });
    return (
        <svg viewBox={`0 0 ${W} ${HR}`} className="qv-chart__svg qv-chart__svg--repere" role="img" aria-label={data.title || 'Courbe dans un repère'}>
            <defs>
                <clipPath id={clip}><rect x={MR.gauche} y={MR.haut} width={W - MR.gauche - MR.droite} height={HR - MR.haut - MR.bas} /></clipPath>
            </defs>
            {tx.map(v => <line key={'gx' + v} x1={px(v)} x2={px(v)} y1={MR.haut} y2={HR - MR.bas} className="qv-chart__grid" />)}
            {ty.map(v => <line key={'gy' + v} x1={MR.gauche} x2={W - MR.droite} y1={py(v)} y2={py(v)} className="qv-chart__grid" />)}
            <line x1={MR.gauche} x2={W - MR.droite + 6} y1={ax} y2={ax} className="qv-chart__axis qv-chart__axis--repere" />
            <line x1={ay} x2={ay} y1={HR - MR.bas} y2={MR.haut - 6} className="qv-chart__axis qv-chart__axis--repere" />
            <polygon points={`${W - MR.droite + 8},${ax} ${W - MR.droite + 1},${ax - 3.5} ${W - MR.droite + 1},${ax + 3.5}`} className="qv-chart__fleche" />
            <polygon points={`${ay},${MR.haut - 8} ${ay - 3.5},${MR.haut - 1} ${ay + 3.5},${MR.haut - 1}`} className="qv-chart__fleche" />
            {tx.filter(v => v !== 0).map(v => <text key={'tx' + v} x={px(v)} y={ax + 13} textAnchor="middle" className="qv-chart__tick">{nombre(v)}</text>)}
            {ty.filter(v => v !== 0).map(v => <text key={'ty' + v} x={ay - 5} y={py(v) + 3.5} textAnchor="end" className="qv-chart__tick">{nombre(v)}</text>)}
            {X.min <= 0 && X.max >= 0 && Y.min <= 0 && Y.max >= 0 && <text x={ay - 5} y={ax + 13} textAnchor="end" className="qv-chart__tick">0</text>}
            {X.label && <text x={W - MR.droite + 4} y={ax - 7} textAnchor="end" className="qv-chart__label">{X.label}</text>}
            {Y.label && <text x={ay + 7} y={MR.haut + 2} className="qv-chart__label">{Y.label}</text>}
            <g clipPath={`url(#${clip})`}>
                {courbes.map(({ c, pts, couleur }, i) => pts.length > 1 && (
                    <polyline key={i} points={pts.map(([x, y]) => `${px(x)},${py(y)}`).join(' ')} fill="none" stroke={couleur}
                        strokeWidth="2.5" strokeLinejoin="round" strokeDasharray={c.style === 'dashed' ? '7 5' : undefined} />
                ))}
            </g>
            {courbes.map(({ c, fin, couleur }, i) => c.name && fin && (
                <text key={'n' + i} x={Math.min(px(fin[0]) + 5, W - MR.droite - 4)} y={Math.max(py(fin[1]) - 6, MR.haut + 10)}
                    textAnchor={px(fin[0]) > W - 60 ? 'end' : 'start'} className="qv-chart__value" fill={couleur}>{c.name}</text>
            ))}
            {(data.marks || []).filter(m => typeof m.x === 'number' && typeof m.y === 'number').map((m, i) => (
                <g key={'m' + i}>
                    <circle cx={px(m.x)} cy={py(m.y)} r="3.8" className="qv-chart__mark" />
                    {m.label && (() => {
                        // pos : coin où écrire l'étiquette (nw par défaut, au-dessus à gauche du point).
                        const [dx, dy, ancre] = { ne: [7, -7, 'start'], nw: [-7, -7, 'end'], se: [7, 15, 'start'], sw: [-7, 15, 'end'] }[m.pos] || [-7, -7, 'end'];
                        return <text x={px(m.x) + dx} y={py(m.y) + dy} textAnchor={ancre} className="qv-chart__value">{m.label}</text>;
                    })()}
                </g>
            ))}
        </svg>
    );
}

function Camembert({ data }) {
    const labels = data.labels || [];
    const valeurs = (data.series?.[0]?.values || []).filter(v => typeof v === 'number');
    const total = valeurs.reduce((a, b) => a + b, 0);
    if (!valeurs.length || total <= 0) return null;
    const R = 90;
    const C = 100;
    let angle = -Math.PI / 2;
    const parts = valeurs.map((v, i) => {
        const a0 = angle;
        const a1 = angle + (v / total) * 2 * Math.PI;
        angle = a1;
        const grand = a1 - a0 > Math.PI ? 1 : 0;
        const p = (a, r = R) => `${C + r * Math.cos(a)},${C + r * Math.sin(a)}`;
        const d = valeurs.length === 1
            ? `M ${C - R},${C} a ${R},${R} 0 1,0 ${2 * R},0 a ${R},${R} 0 1,0 ${-2 * R},0`
            : `M ${C},${C} L ${p(a0)} A ${R},${R} 0 ${grand},1 ${p(a1)} Z`;
        const milieu = (a0 + a1) / 2;
        return { d, couleur: COULEURS[i % COULEURS.length], label: labels[i], v, milieu, large: a1 - a0 > 0.35 };
    });
    return (
        <div className="qv-chart__pie">
            <svg viewBox="0 0 200 200" className="qv-chart__svg qv-chart__svg--pie" role="img" aria-label={data.title || 'Graphique circulaire'}>
                {parts.map((p, i) => <path key={i} d={p.d} fill={p.couleur} stroke="#fff" strokeWidth="1.5" />)}
                {parts.filter(p => p.large).map((p, i) => (
                    <text key={i} x={C + R * 0.62 * Math.cos(p.milieu)} y={C + R * 0.62 * Math.sin(p.milieu) + 4} textAnchor="middle" className="qv-chart__slice">
                        {nombre(p.v)}{data.unit === '%' ? ' %' : ''}
                    </text>
                ))}
            </svg>
            <ul className="qv-chart__legend qv-chart__legend--pie">
                {parts.map((p, i) => (
                    <li key={i}><span className="qv-chart__swatch" style={{ background: p.couleur }} />{p.label} : <strong>{avecUnite(p.v, data.unit)}</strong></li>
                ))}
            </ul>
        </div>
    );
}

export default function Chart({ data }) {
    const kind = ['bar', 'line', 'pie', 'xy'].includes(data?.kind) ? data.kind : 'bar';
    const series = (data?.series || []).slice(0, kind === 'pie' ? 1 : 2);
    return (
        <figure className="qv-chart">
            {data?.title && <figcaption className="qv-chart__title">{data.title}{data.unit && kind !== 'pie' && !data.title.toLowerCase().includes(data.unit.toLowerCase()) ? ` (en ${data.unit})` : ''}</figcaption>}
            {kind !== 'pie' && kind !== 'xy' && series.length > 1 && (
                <ul className="qv-chart__legend">
                    {series.map((s, i) => <li key={i}><span className="qv-chart__swatch" style={{ background: COULEURS[i] }} />{s.name}</li>)}
                </ul>
            )}
            {kind === 'pie' ? <Camembert data={data} /> : kind === 'xy' ? <Repere data={data} /> : <BarresOuCourbe data={data} kind={kind} />}
            {data?.source && <div className="qv-chart__source">Source : {data.source}</div>}
        </figure>
    );
}
